import React from 'react';
import { Container, Typography, Box, Link, Grid, Paper } from '@mui/material';
import { motion } from 'framer-motion';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import LanguageIcon from '@mui/icons-material/Language';
import GitHubIcon from '@mui/icons-material/GitHub';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import CodeIcon from '@mui/icons-material/Code';

const ContactItem = ({ icon, title, detail, href }) => {
  
  const content = (
    <Paper
      className="glass-card"
      sx={{
        p: { xs: 2, md: 3 },
        height: { xs: '180px', sm: '200px' }, // Responsive height
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 2,
        bgcolor: 'transparent',
        border: 'none',
        boxShadow: 'none',
        '&:hover': {
          transform: 'none',
          boxShadow: 'none',
          '& .contact-icon-box': {
            transform: 'scale(1.1) rotate(10deg)',
            bgcolor: 'rgba(255, 255, 255, 0.15)'
          }
        }
      }}
    >
      <Box
        className="contact-icon-box"
        sx={{
          color: 'var(--primary)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '60px',
          height: '60px',
          borderRadius: '50%',
          bgcolor: 'rgba(255, 255, 255, 0.05)',
          transition: 'all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
        }}
      >
        <motion.div
          animate={{
            scale: [1, 1.1, 1],
          }}
          transition={{
            duration: 3,
            repeat: Infinity,
            ease: "easeInOut"
          }}
          style={{ display: 'flex' }}
        >
          {icon}
        </motion.div>
      </Box>
      <Typography
        variant="h6"
        sx={{
          color: '#ffffff',
          fontWeight: 700,
          textAlign: 'center',
          fontSize: { xs: '1.25rem', sm: '1.45rem' }
        }}
      >
        {title}
      </Typography>
      <Typography
        variant="body1"
        sx={{
          color: 'var(--text-dim)',
          textAlign: 'center',
          fontSize: { xs: '1rem', sm: '1.1rem' },
          opacity: 0.9
        }}
      >
        {detail}
      </Typography>
    </Paper>
  );

  if (href) {
    return (
      <Link
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        sx={{ textDecoration: 'none' }}
      >
        {content}
      </Link>
    );
  }

  return content;
};

const Contact = () => {
  const contactInfo = [
    {
      icon: <EmailIcon fontSize="large" />,
      title: 'Email',
      detail: 'onkar2654@gmail.com',
      href: 'mailto:onkar2654@gmail.com'
    },
    {
      icon: <PhoneIcon fontSize="large" />,
      title: 'Phone',
      detail: '+91 9370928398',
      href: 'tel:+919370928398'
    },
    {
      icon: <LocationOnIcon fontSize="large" />,
      title: 'Location',
      detail: 'Pune, Maharashtra, India'
    },
    {
      icon: <LinkedInIcon fontSize="large" />,
      title: 'LinkedIn',
      detail: 'linkedin.com/in/onkar-wagh',
      href: 'https://www.linkedin.com/in/onkar-wagh-632ab821a'
    },
    {
      icon: <CodeIcon fontSize="large" />,
      title: 'LeetCode',
      detail: 'leetcode.com/u/onkar',
      href: 'https://leetcode.com/u/user9833Cl/'
    },
    {
      icon: <LanguageIcon fontSize="large" />,
      title: 'Portfolio',
      detail: 'cd-onkar.vercel.app',
      href: 'https://cd-onkar.vercel.app'
    },
    {
      icon: <GitHubIcon fontSize="large" />,
      title: 'GitHub',
      detail: 'github.com/onkarrw',
      href: 'https://github.com/onkarrw'
    }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        type: "spring",
        stiffness: 180,
        damping: 15
      }
    }
  };

  return (
    <Box 
      component="section"
      sx={{ 
        py: { xs: 6, md: 12 },
        minHeight: { xs: 'auto', md: '100vh' },
        display: 'flex',
        alignItems: 'center',
        background: 'transparent',
        backdropFilter: 'blur(10px)',
        borderRadius: 2,
        boxShadow: 'none'
      }}
    >
      <Container maxWidth="lg">
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.1 }}
        >
          <motion.div variants={itemVariants}>
            <Typography 
              variant="h3" 
              gutterBottom 
              align="center" 
              sx={{ 
                mb: 6,
                fontFamily: "'Chivo Mono', monospace",
                fontSize: { xs: '2rem', sm: '2.5rem', md: '3.5rem' },
                fontWeight: 700,
                background: 'linear-gradient(135deg, #ffffff, rgba(255, 255, 255, 0.7))',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                filter: 'drop-shadow(0 0 10px rgba(255, 255, 255, 0.1))',
                opacity: 1
              }}
            >
              Get in Touch
            </Typography>
          </motion.div>

          <Grid 
            container 
            spacing={{ xs: 2, md: 4 }} 
            sx={{ 
              justifyContent: 'center',
              px: { xs: 1, md: 4 }
            }}
          >
            {contactInfo.map((info, index) => (
              <Grid 
                item 
                xs={12} 
                sm={6} 
                md={4} 
                key={index}
                sx={{
                  display: 'flex',
                  justifyContent: 'center'
                }}
              >
                <motion.div
                  variants={itemVariants}
                  style={{ width: '100%', maxWidth: '320px' }}
                >
                  <ContactItem {...info} />
                </motion.div>
              </Grid>
            ))}
          </Grid>
        </motion.div>
      </Container>
    </Box>
  );
};

export default Contact; 